import { Target, Crosshair, Gauge, AlertTriangle } from 'lucide-react';
import { cn, formatPercentage, getMetricColor, getMetricBg } from '@/lib/utils';
import type { PhaseMetrics } from '@/types/dashboard';

interface MetricCardsProps {
  phase2: PhaseMetrics;
  rerankerDamageCount: number;
  runsPerDataset?: number;
}

export function MetricCards({ phase2, rerankerDamageCount, runsPerDataset }: MetricCardsProps) {
  const recall = phase2.recall;
  const precision = phase2.precision;
  const f1 = phase2.f1;
  const isMultiRun = (runsPerDataset ?? 1) > 1;

  const cards = [
    {
      label: 'Recall',
      sublabel: isMultiRun ? `Mean of ${runsPerDataset} runs` : 'Phase 2',
      value: formatPercentage(recall),
      stddev: phase2.recall_stddev,
      rawValue: recall,
      icon: Target,
      description: 'True positives found',
    },
    {
      label: 'Precision',
      sublabel: isMultiRun ? `Mean of ${runsPerDataset} runs` : 'Phase 2',
      value: formatPercentage(precision),
      stddev: phase2.precision_stddev,
      rawValue: precision,
      icon: Crosshair,
      description: 'Results that are relevant',
    },
    {
      label: 'F1 Score',
      sublabel: isMultiRun ? `Mean of ${runsPerDataset} runs` : 'Phase 2',
      value: formatPercentage(f1),
      stddev: phase2.f1_stddev,
      rawValue: f1,
      icon: Gauge,
      description: 'Harmonic mean of P & R',
    },
    {
      label: 'Reranker Damage',
      sublabel: 'Killed positives',
      value: String(rerankerDamageCount),
      stddev: undefined as number | undefined,
      rawValue: rerankerDamageCount === 0 ? 100 : 0,
      icon: AlertTriangle,
      description: rerankerDamageCount === 0 ? 'No positives harmed' : `${rerankerDamageCount} true positive${rerankerDamageCount !== 1 ? 's' : ''} removed`,
      isDamage: true,
    },
  ];

  return (
    <div className="grid grid-cols-4 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        const colorClass = card.isDamage
          ? rerankerDamageCount === 0
            ? 'text-green-400'
            : 'text-red-400'
          : getMetricColor(card.rawValue);
        const bgClass = card.isDamage
          ? rerankerDamageCount === 0
            ? 'bg-green-500/15 border-green-500/30'
            : 'bg-red-500/15 border-red-500/30'
          : getMetricBg(card.rawValue);

        return (
          <div
            key={card.label}
            className={cn(
              'rounded-xl border p-5 backdrop-blur-xs transition-all duration-200 hover:scale-[1.02]',
              bgClass
            )}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {card.label}
                </div>
                <div className="text-[10px] text-muted-foreground/60 mt-0.5">
                  {card.sublabel}
                </div>
              </div>
              <Icon className={cn('h-5 w-5 opacity-60', colorClass)} />
            </div>
            <div className="flex items-baseline gap-2">
              <div className={cn('text-4xl font-bold tabular-nums tracking-tight', colorClass)}>
                {card.value}
              </div>
              {isMultiRun && card.stddev != null && card.stddev > 0 && (
                <div className="text-sm font-mono text-muted-foreground/70">
                  &plusmn;{card.stddev.toFixed(1)}
                </div>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              {card.description}
            </div>
          </div>
        );
      })}
    </div>
  );
}
